import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, documentId } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';
import GiftButton from '../Gift/GiftButton';
import { GIFT_CONTEXTS } from '../../../utils/nurseGiftManager';

const SwapRequestsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { createPlanVersionWithSwaps } = useShiftPlan();
  const [swapRequests, setSwapRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSwaps, setSelectedSwaps] = useState([]); // Array of swap IDs
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!currentTeam || !currentUser) return;

    const fetchSwapRequests = async () => {
      try {
        setLoading(true);
        // Fetch swap requests that are MEMBER_APPROVED (waiting for leader approval)
        const swapsRef = collection(db, 'shiftSwaps');
        const q = query(
          swapsRef,
          where('teamId', '==', currentTeam.id),
          where('status', '==', 'MEMBER_APPROVED'),
          where('planId', '!=', null)
        );
        
        const snapshot = await getDocs(q);
        const swaps = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            
            // Fetch user data for requestedBy and requestedTo
            const [requestedByUser, requestedToUser] = await Promise.all([
              getDoc(doc(db, 'users', data.requestedBy)),
              getDoc(doc(db, 'users', data.requestedTo))
            ]);

            return {
              id: doc.id,
              ...data,
              requestedByUser: requestedByUser.exists() ? requestedByUser.data() : null,
              requestedToUser: requestedToUser.exists() ? requestedToUser.data() : null
            };
          })
        );

        setSwapRequests(swaps);
      } catch (err) {
        console.error('Error fetching swap requests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSwapRequests();
  }, [currentTeam, currentUser]);

  // Check if user is team leader
  const isTeamLeader = currentTeam && (
    currentTeam.leaderId === currentUser?.uid || 
    currentTeam.createdBy === currentUser?.uid
  );

  if (!isTeamLeader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับ
          </button>
        </div>
      </div>
    );
  }

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '🌅';
      case SHIFT_TYPES.AFTERNOON: return '☀️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '📅';
    }
  };

  const toggleSwapSelection = (swapId) => {
    setSelectedSwaps(prev => {
      if (prev.includes(swapId)) {
        return prev.filter(id => id !== swapId);
      } else {
        return [...prev, swapId];
      }
    });
  };

  const selectAllSwaps = () => {
    setSelectedSwaps(swapRequests.map(s => s.id));
  };

  const deselectAllSwaps = () => {
    setSelectedSwaps([]);
  };

  const handleApproveSwaps = async () => {
    if (selectedSwaps.length === 0) {
      alert('กรุณาเลือกดีลที่ต้องการอนุมัติ');
      return;
    }

    try {
      setProcessing(true);

      // Group swaps by planId
      const swapsByPlan = {};
      selectedSwaps.forEach(swapId => {
        const swap = swapRequests.find(s => s.id === swapId);
        if (swap && swap.planId) {
          if (!swapsByPlan[swap.planId]) {
            swapsByPlan[swap.planId] = [];
          }
          swapsByPlan[swap.planId].push(swap);
        }
      });

      // Create plan version for each plan
      for (const [planId, swaps] of Object.entries(swapsByPlan)) {
        await createPlanVersionWithSwaps(planId, swaps.map(s => s.id));
      }

      // Update swap statuses
      for (const swapId of selectedSwaps) {
        const swapRef = doc(db, 'shiftSwaps', swapId);
        await updateDoc(swapRef, {
          status: 'APPROVED',
          approvedBy: currentUser.uid,
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      alert(`อนุมัติ ${selectedSwaps.length} ดีลสำเร็จ และสร้างแผน version ใหม่แล้ว`);
      setSelectedSwaps([]);
      // Refresh swap requests
      window.location.reload();
    } catch (err) {
      console.error('Error approving swaps:', err);
      alert('เกิดข้อผิดพลาดในการอนุมัติ: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectSwap = async (swapId) => {
    if (!window.confirm('คุณต้องการปฏิเสธดีลนี้หรือไม่?')) return;

    try {
      const swapRef = doc(db, 'shiftSwaps', swapId);
      await updateDoc(swapRef, {
        status: 'REJECTED',
        approvedBy: currentUser.uid,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      alert('ปฏิเสธดีลสำเร็จ');
      // Refresh swap requests
      window.location.reload();
    } catch (err) {
      console.error('Error rejecting swap:', err);
      alert('เกิดข้อผิดพลาดในการปฏิเสธ: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">คำขอแลกเวร</h1>
              <p className="text-sm text-gray-600 mt-1">
                รายการดีลที่รอการอนุมัติ ({swapRequests.length} ดีล)
              </p>
            </div>
            <div className="flex gap-2">
              {swapRequests.length > 0 && (
                <>
                  <button
                    onClick={selectAllSwaps}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                  >
                    เลือกทั้งหมด
                  </button>
                  <button
                    onClick={deselectAllSwaps}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                  >
                    ยกเลิกทั้งหมด
                  </button>
                  {selectedSwaps.length > 0 && (
                    <button
                      onClick={handleApproveSwaps}
                      disabled={processing}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      {processing ? 'กำลังประมวลผล...' : `อนุมัติ ${selectedSwaps.length} ดีล`}
                    </button>
                  )}
                </>
              )}
              <button
                onClick={() => navigate('/nurse/shifts')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                กลับ
              </button>
            </div>
          </div>
        </div>

        {/* Swap Requests List */}
        {swapRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">ไม่มีคำขอแลกเวรรอการอนุมัติ</p>
          </div>
        ) : (
          <div className="space-y-4">
            {swapRequests.map((swap) => {
              const isSelected = selectedSwaps.includes(swap.id);
              const originalShiftDate = swap.originalShift?.startTime?.toDate ? 
                swap.originalShift.startTime.toDate() : null;
              const requestedShiftDate = swap.requestedShiftId ? null : null; // TODO: Fetch if needed

              return (
                <div
                  key={swap.id}
                  className={`bg-white rounded-lg shadow-sm p-6 border-2 ${
                    isSelected ? 'border-green-500' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSwapSelection(swap.id)}
                      className="mt-1 w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />

                    {/* Swap Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">{getShiftTypeIcon(swap.originalShift?.shiftType)}</span>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {swap.requestedByName} ↔ {swap.requestedToName}
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Original Shift */}
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">เวรที่ต้องการแลก</p>
                          <p className="font-semibold text-gray-900">
                            {swap.requestedByName} - เวร{getShiftTypeLabel(swap.originalShift?.shiftType)}
                          </p>
                          {originalShiftDate && (
                            <p className="text-sm text-gray-600 mt-1">
                              {formatDateTime(swap.originalShift.startTime)}
                            </p>
                          )}
                        </div>

                        {/* Requested Shift */}
                        {swap.requestedShiftId ? (
                          <div className="bg-green-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">เวรที่เสนอแลก</p>
                            <p className="font-semibold text-gray-900">
                              {swap.requestedToName} - เวร{getShiftTypeLabel(swap.requestedShift?.shiftType || '')}
                            </p>
                            {swap.requestedShift?.startTime && (
                              <p className="text-sm text-gray-600 mt-1">
                                {formatDateTime(swap.requestedShift.startTime)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">แลกแบบ one-way</p>
                            <p className="font-semibold text-gray-900">
                              {swap.requestedToName} จะได้รับเวรของ {swap.requestedByName}
                            </p>
                          </div>
                        )}
                      </div>

                      {swap.message && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-sm text-gray-600 mb-1">ข้อความ:</p>
                          <p className="text-sm text-gray-900">{swap.message}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>ส่งคำขอเมื่อ: {formatDateTime(swap.createdAt)}</span>
                        {swap.memberApprovedAt && (
                          <span>• ตอบรับเมื่อ: {formatDateTime(swap.memberApprovedAt)}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <GiftButton
                        toUserId={swap.requestedBy}
                        toUserName={swap.requestedByName}
                        context={GIFT_CONTEXTS.SHIFT_SWAP}
                        relatedId={swap.id}
                        relatedType="shiftSwap"
                        variant="small"
                        className="w-full"
                      />
                      <button
                        onClick={() => handleRejectSwap(swap.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                      >
                        ปฏิเสธ
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapRequestsPage;

